import React, { useEffect, useState } from "react";
import AppContainer from "../../Components/Accounting/AppContainer";
import DataTable from "../../Components/Accounting/DataTable";
import Select2 from "../../Components/Accounting/Select2";
import ajaxAccounting, { ajaxItems } from "../../util/accounting/remote/ajaxAccounting";
import functions from "../../util/accounting/functions";
import toast, { Toaster } from "react-hot-toast";
import AppModal from "../../Components/Accounting/AppModal";

const emptyForm = () => ({
  code: "", name: "", category: "service", description: "",
  default_account_id: "", default_price: "", default_tax_rate_id: "",
  cost_price: "", inventory_account_id: "", cogs_account_id: "", reorder_level: "",
});

const SalesItems = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [incomeAccounts, setIncomeAccounts] = useState([]);
  const [assetAccounts, setAssetAccounts] = useState([]);
  const [expenseAccounts, setExpenseAccounts] = useState([]);
  const [taxRates, setTaxRates] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editModal, setEditModal] = useState(null);
  const [adjustModal, setAdjustModal] = useState(null);
  const [adjustDelta, setAdjustDelta] = useState("");
  const [adjustReason, setAdjustReason] = useState("");
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchItems(); fetchMeta(); }, [lowStockOnly]); // eslint-disable-line

  const fetchItems = async () => {
    setLoading(true);
    const res = lowStockOnly
      ? await ajaxItems.getLowStockSalesItems()
      : await ajaxItems.listSalesItems({});
    if (res.status === "OK") setItems(Array.isArray(res.details) ? res.details : []);
    else if (res.status !== "Fail") toast.error(res.message);
    else setItems([]);
    setLoading(false);
  };

  const fetchMeta = async () => {
    const [inc, ast, exp, tax] = await Promise.all([
      ajaxAccounting.listAccounts({ type: "income", postable_only: 1 }),
      ajaxAccounting.listAccounts({ type: "asset", postable_only: 1 }),
      ajaxAccounting.listAccounts({ type: "expense", postable_only: 1 }),
      ajaxAccounting.listTaxes(),
    ]);
    if (inc.status === "OK") setIncomeAccounts(inc.details || []);
    if (ast.status === "OK") setAssetAccounts(ast.details || []);
    if (exp.status === "OK") setExpenseAccounts(exp.details || []);
    if (tax.status === "OK") setTaxRates(tax.details || []);
  };

  const acctOptions = (list) => list.map((a) => ({ value: String(a.id), label: `${a.account_code} — ${a.name}` }));
  const taxOptions = [{ value: "", label: "No tax" }, ...taxRates.map((t) => ({ value: String(t.id), label: `${t.name} (${t.rate}%)` }))];
  const categoryOptions = [{ value: "service", label: "Service" }, { value: "product", label: "Product (tracks stock)" }];

  const buildPayload = (f) => ({
    code: f.code, name: f.name, category: f.category, description: f.description,
    default_account_id: f.default_account_id ? parseInt(f.default_account_id) : null,
    default_price: parseFloat(f.default_price) || 0,
    default_tax_rate_id: f.default_tax_rate_id ? parseInt(f.default_tax_rate_id) : null,
    cost_price: f.category === "product" ? (parseFloat(f.cost_price) || 0) : null,
    inventory_account_id: f.category === "product" && f.inventory_account_id ? parseInt(f.inventory_account_id) : null,
    cogs_account_id: f.category === "product" && f.cogs_account_id ? parseInt(f.cogs_account_id) : null,
    reorder_level: f.category === "product" && f.reorder_level !== "" ? parseFloat(f.reorder_level) : null,
  });

  const handleCreate = async () => {
    if (!form.code || !form.name || !form.default_account_id) { toast.error("Code, name, and revenue account are required."); return; }
    if (form.category === "product" && (!form.inventory_account_id || !form.cogs_account_id)) {
      toast.error("Product items need an inventory account and a COGS account.");
      return;
    }
    setSaving(true);
    const res = await ajaxItems.createSalesItem(buildPayload(form));
    if (res.status === "OK") {
      toast.success("Item created!");
      fetchItems();
      setShowModal(false);
      setForm(emptyForm());
    } else toast.error(res.message);
    setSaving(false);
  };

  const openEdit = (row) => setEditModal({
    id: row.id, code: row.code, name: row.name, category: row.category, description: row.description || "",
    default_account_id: String(row.default_account_id), default_price: row.default_price,
    default_tax_rate_id: row.default_tax_rate_id ? String(row.default_tax_rate_id) : "",
    cost_price: row.cost_price ?? "", inventory_account_id: row.inventory_account_id ? String(row.inventory_account_id) : "",
    cogs_account_id: row.cogs_account_id ? String(row.cogs_account_id) : "", reorder_level: row.reorder_level ?? "",
  });

  const handleUpdate = async () => {
    setSaving(true);
    const res = await ajaxItems.updateSalesItem({ id: editModal.id, ...buildPayload(editModal) });
    if (res.status === "OK") { toast.success("Item updated!"); fetchItems(); setEditModal(null); }
    else toast.error(res.message);
    setSaving(false);
  };

  const handleAdjust = async () => {
    if (!adjustDelta) { toast.error("Enter a quantity change."); return; }
    setSaving(true);
    const res = await ajaxItems.adjustSalesItemStock({ id: adjustModal.id, delta: parseFloat(adjustDelta), reason: adjustReason });
    if (res.status === "OK") { toast.success("Stock adjusted!"); fetchItems(); setAdjustModal(null); setAdjustDelta(""); setAdjustReason(""); }
    else toast.error(res.message);
    setSaving(false);
  };

  const columns = [
    { key: "code", label: "Code", render: (v) => <code>{v}</code> },
    { key: "name", label: "Name", render: (v) => <strong>{v}</strong> },
    { key: "category", label: "Category", render: (v) => <span className={`badge bg-${v === "product" ? "primary" : "secondary"} text-capitalize`}>{v}</span> },
    { key: "default_price", label: "Price", render: (v) => functions.formatCurrency(v) },
    {
      key: "quantity_on_hand", label: "Stock", render: (v, row) => row.category !== "product" ? "—" :
        <span className={row.reorder_level != null && v <= row.reorder_level ? "tx-danger fw-bold" : ""}>{v}</span>,
    },
    {
      key: "id", label: "Actions",
      render: (id, row) => (
        <div className="d-flex gap-1">
          {row.category === "product" && (
            <button className="btn btn-sm btn-light" title="Adjust Stock" onClick={() => setAdjustModal(row)}><i className="fa-solid fa-boxes-stacked"></i></button>
          )}
          <button className="btn btn-sm btn-outline-primary" title="Edit" onClick={() => openEdit(row)}><i className="fa-solid fa-pen"></i></button>
        </div>
      ),
    },
  ];

  return (
    <AppContainer
      title="Products / Services"
      previousPageTitle="Accounting"
      description="Reusable catalog for sales invoice lines. Product items track stock and post COGS automatically when sold; service items post revenue only."
      actions={[{ label: "New Item", icon: "fa-solid fa-plus", className: "btn btn-primary", onClick: () => setShowModal(true) }]}
    >
      <Toaster />
      <div className="card custom-card p-3 mb-3">
        <div className="form-check">
          <input className="form-check-input" type="checkbox" id="lowStockOnly" checked={lowStockOnly} onChange={(e) => setLowStockOnly(e.target.checked)} />
          <label className="form-check-label tx-13" htmlFor="lowStockOnly">Show only items at or below reorder level</label>
        </div>
      </div>
      <div className="card custom-card p-3">
        <DataTable columns={columns} data={items} loading={loading} pageSize={15} emptyMessage="No items found." />
      </div>

      {(showModal || editModal) && (
        <AppModal
          title={<>{editModal ? "Edit Item" : "New Item"}</>}
          size="lg"
          onClose={() => { setShowModal(false); setEditModal(null); }}
          footer={
            <>
              <button className="btn btn-light" onClick={() => { setShowModal(false); setEditModal(null); }}>Cancel</button>
              <button className="btn btn-primary" onClick={editModal ? handleUpdate : handleCreate} disabled={saving}>
                {saving ? "Saving..." : editModal ? "Save Changes" : "Create Item"}
              </button>
            </>
          }
        >
          <ItemForm
            f={editModal || form}
            setF={editModal ? setEditModal : setForm}
            isEdit={!!editModal}
            categoryOptions={categoryOptions}
            acctOptions={acctOptions}
            incomeAccounts={incomeAccounts}
            assetAccounts={assetAccounts}
            expenseAccounts={expenseAccounts}
            taxOptions={taxOptions}
          />
        </AppModal>
      )}

      {adjustModal && (
        <AppModal
          title={<>Adjust Stock — {adjustModal.name}</>}
          onClose={() => setAdjustModal(null)}
          footer={
            <>
              <button className="btn btn-light" onClick={() => setAdjustModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleAdjust} disabled={saving}>{saving ? "Saving..." : "Apply Adjustment"}</button>
            </>
          }
        >
          <p className="tx-13 tx-muted">Current balance: <strong>{adjustModal.quantity_on_hand}</strong></p>
          <label className="form-label tx-12 fw-semibold">Quantity Change <span className="tx-danger">*</span></label>
          <input type="number" className="form-control mb-3" placeholder="e.g. 50 or -10" value={adjustDelta} onChange={(e) => setAdjustDelta(e.target.value)} />
          <label className="form-label tx-12 fw-semibold">Reason</label>
          <input className="form-control" placeholder="e.g. Stocktake correction" value={adjustReason} onChange={(e) => setAdjustReason(e.target.value)} />
        </AppModal>
      )}
    </AppContainer>
  );
};

const ItemForm = ({ f, setF, isEdit, categoryOptions, acctOptions, incomeAccounts, assetAccounts, expenseAccounts, taxOptions }) => (
  <div className="row g-3">
    <div className="col-lg-4">
      <label className="form-label tx-12 fw-semibold">Code <span className="tx-danger">*</span></label>
      <input className="form-control" value={f.code} disabled={isEdit} onChange={(e) => setF({ ...f, code: e.target.value })} placeholder="e.g. WIDGET-001" />
    </div>
    <div className="col-lg-8">
      <label className="form-label tx-12 fw-semibold">Name <span className="tx-danger">*</span></label>
      <input className="form-control" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
    </div>
    <div className="col-lg-4">
      <label className="form-label tx-12 fw-semibold">Category <span className="tx-danger">*</span></label>
      <Select2 options={categoryOptions} value={f.category} onChange={(v) => setF({ ...f, category: v })} />
    </div>
    <div className="col-lg-4">
      <label className="form-label tx-12 fw-semibold">Default Price</label>
      <input type="number" className="form-control" value={f.default_price} onChange={(e) => setF({ ...f, default_price: e.target.value })} />
    </div>
    <div className="col-lg-4">
      <label className="form-label tx-12 fw-semibold">Default Tax Rate</label>
      <Select2 options={taxOptions} value={f.default_tax_rate_id} onChange={(v) => setF({ ...f, default_tax_rate_id: v })} />
    </div>
    <div className="col-12">
      <label className="form-label tx-12 fw-semibold">Revenue Account <span className="tx-danger">*</span></label>
      <Select2 options={acctOptions(incomeAccounts)} value={f.default_account_id} onChange={(v) => setF({ ...f, default_account_id: v })} placeholder="Select income account" />
    </div>
    <div className="col-12">
      <label className="form-label tx-12 fw-semibold">Description</label>
      <textarea className="form-control" rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
    </div>
    {f.category === "product" && (
      <>
        <div className="col-12"><hr className="my-1" /><p className="tx-12 tx-muted fw-semibold mb-0">Stock & COGS (required for products)</p></div>
        <div className="col-lg-4">
          <label className="form-label tx-12 fw-semibold">Cost Price <span className="tx-danger">*</span></label>
          <input type="number" className="form-control" value={f.cost_price} onChange={(e) => setF({ ...f, cost_price: e.target.value })} />
        </div>
        <div className="col-lg-4">
          <label className="form-label tx-12 fw-semibold">Inventory Account <span className="tx-danger">*</span></label>
          <Select2 options={acctOptions(assetAccounts)} value={f.inventory_account_id} onChange={(v) => setF({ ...f, inventory_account_id: v })} placeholder="Select asset account" />
        </div>
        <div className="col-lg-4">
          <label className="form-label tx-12 fw-semibold">COGS Account <span className="tx-danger">*</span></label>
          <Select2 options={acctOptions(expenseAccounts)} value={f.cogs_account_id} onChange={(v) => setF({ ...f, cogs_account_id: v })} placeholder="Select expense account" />
        </div>
        <div className="col-lg-4">
          <label className="form-label tx-12 fw-semibold">Reorder Level</label>
          <input type="number" className="form-control" value={f.reorder_level} onChange={(e) => setF({ ...f, reorder_level: e.target.value })} placeholder="Alert when stock falls to/below this" />
        </div>
      </>
    )}
  </div>
);

export default SalesItems;
